import type { ObjectTypeKey } from "schemas/objectType";

import { skipToken } from "@tanstack/react-query";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { agentIntegrationsRouter } from "@v2/trpc/routes/agentIntegrations";

interface IntegrationObjectSearchResult {
  id: string;
  title?: string;
  handle?: string;
  imageUrl?: string | null;
  subtitle?: string;
}

interface UseIntegrationObjectSearchOptions {
  integrationKey: string;
  objectType: ObjectTypeKey;
  query: string;
  enabled?: boolean;
}

interface UseIntegrationObjectSearchReturn {
  results: IntegrationObjectSearchResult[];
  isLoading: boolean;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
}

export function useIntegrationObjectSearch({
  integrationKey,
  objectType,
  query,
  enabled = true,
}: UseIntegrationObjectSearchOptions): UseIntegrationObjectSearchReturn {
  const projectId = useCurrentProjectId();

  const shouldFetch = Boolean(projectId) && enabled;

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    agentIntegrationsRouter.searchObjects.useInfiniteQuery(
      shouldFetch && projectId
        ? {
            projectId,
            integrationKey,
            objectType,
            query: query.length > 0 ? query : undefined,
            limit: 10,
          }
        : skipToken,
      {
        enabled: shouldFetch,
        getNextPageParam: (lastPage) => lastPage.nextCursor,
      },
    );

  const results = data?.pages?.flatMap((page) => page.results) ?? [];

  return {
    results,
    isLoading,
    fetchNextPage: () => void fetchNextPage(),
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
  };
}
